import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { useProgramBroadcasts } from '@/features/broadcast-schedule/api/get-program-broadcasts';
import {
  addAppDays,
  appWallClockParts,
  appWallClockToInstant,
  floorToMinute,
} from '@/utils/spain-time';

import { TProgram, TProgramSchedule } from '../types';
import {
  MAX_GENERATED_CANDIDATES,
  TProgramBroadcastCandidate,
  findSlotsMissingHours,
  generateProgramBroadcastCandidates,
} from '../utils/programBroadcastCandidates';

/**
 * The most emissions one generation will create. Agreed at 100: the ticket's
 * own case is 40 across eight programmes, so a single programme's batch sits
 * well under it, while a mis-set period is refused before a request goes out.
 */
export const PROGRAM_BROADCAST_BATCH_CAP = 100;

/**
 * How many existing emissions the skip lookup asks for. The listing is
 * paginated, so the response's `total` is checked against what came back: a
 * period holding more than this is reported as a period too large to check, and
 * blocks generation rather than skipping only the emissions that fit in one
 * page. It is a different failure from a request that errored — retrying it
 * would answer identically, and the way out is a shorter period.
 *
 * It is the candidate walk's own ceiling rather than a second literal: the page
 * has to hold at least what the walk can produce, and two constants that must
 * agree in separate files agree only until one of them moves.
 */
const EXISTING_EMISSIONS_PAGE_SIZE = MAX_GENERATED_CANDIDATES;

export type TProgramBroadcastGenerationForm = {
  schedule: TProgramSchedule[];
};

export type TGenerationPeriod = {
  start: Date | null;
  end: Date | null;
};

export enum EGenerationBlockReason {
  NoSlots = 'no-slots',
  NoPeriod = 'no-period',
  NoCandidates = 'no-candidates',
  SlotsMissingHours = 'slots-missing-hours',
  LookupPending = 'lookup-pending',
  LookupFailed = 'lookup-failed',
  LookupTooLarge = 'lookup-too-large',
  AllAlreadyExist = 'all-already-exist',
  OverCap = 'over-cap',
  TooManyCandidates = 'too-many-candidates',
}

const EMPTY_PERIOD: TGenerationPeriod = { start: null, end: null };

/**
 * How far past each end of the period the skip lookup reaches. `gte`/`lte` are
 * absolute instants — they narrow `start_datetime` server-side — and a
 * candidate's start always falls inside the Madrid days of the period, so this
 * is a safety margin rather than a correction. A day of slack on each end
 * keeps every emission of the period inside the answer; the extra ones simply
 * never match a candidate.
 */
const PERIOD_LOOKUP_SLACK_DAYS = 1;

/**
 * Bounds the period as whole Madrid calendar days, which is what a candidate's
 * start instant can ever fall inside: the first day from midnight, the last day
 * to the last millisecond before the next one, each widened by the slack above.
 *
 * Both bounds apply to `start_datetime` — checked against the listing's own
 * controller — so a slot crossing midnight, whose end lands on the following
 * day, is still found by the day it starts on.
 */
function toPeriodBounds({ start, end }: TGenerationPeriod) {
  if (!start || !end) return null;

  const firstDay = appWallClockParts(start).date;
  const lastDay = appWallClockParts(end).date;

  if (lastDay < firstDay) return null;

  const from = appWallClockToInstant(addAppDays(firstDay, -PERIOD_LOOKUP_SLACK_DAYS), '00:00:00');
  const to = appWallClockToInstant(addAppDays(lastDay, PERIOD_LOOKUP_SLACK_DAYS), '23:59:59.999');

  return { gte: from.toISOString(), lte: to.toISOString() };
}

type TUseProgramBroadcastGenerationParams = {
  program: TProgram | null;
  isOpen: boolean;
};

/**
 * Holds everything the generation modal decides before it creates anything: the
 * editable slot list, the period, the emissions those two produce, the ones
 * that already exist, and therefore the count that will actually be created.
 *
 * The slot list is prefilled from the programme and is local to the generation.
 * Nothing here writes it back — this hook issues no mutation against the
 * programme at all.
 */
export function useProgramBroadcastGeneration({
  program,
  isOpen,
}: TUseProgramBroadcastGenerationParams) {
  const form = useForm<TProgramBroadcastGenerationForm>({
    defaultValues: { schedule: program?.schedule ?? [] },
  });
  const [period, setPeriod] = useState<TGenerationPeriod>(EMPTY_PERIOD);

  const { reset } = form;

  /**
   * Re-seeds the modal from the programme every time it opens, so abandoned
   * edits never survive into the next generation.
   */
  useEffect(() => {
    if (!isOpen) return;

    reset({ schedule: program?.schedule ?? [] });
    setPeriod(EMPTY_PERIOD);
  }, [isOpen, program?.id, reset]);

  const slots = useWatch({ control: form.control, name: 'schedule' }) ?? [];

  const periodBounds = useMemo(() => toPeriodBounds(period), [period]);

  const { candidates, duplicatesDropped, isTruncated } = useMemo(() => {
    if (!period.start || !period.end) {
      return {
        candidates: [] as TProgramBroadcastCandidate[],
        duplicatesDropped: 0,
        isTruncated: false,
      };
    }

    return generateProgramBroadcastCandidates({
      slots,
      startDate: period.start,
      endDate: period.end,
    });
  }, [slots, period]);

  /**
   * A period that produced more candidates than the walk will materialise
   * cannot be generated and cannot be checked either, so it never reaches the
   * listing: asking would cost a thousand-row request to answer a question
   * already settled.
   */
  const isLookupEnabled = Boolean(
    isOpen && program && periodBounds && candidates.length && !isTruncated
  );

  const {
    data: existingEmissions,
    isFetching: isLookupFetching,
    isError: isLookupError,
    refetch: refetchExisting,
  } = useProgramBroadcasts({
    params: {
      programId: program?.id,
      gte: periodBounds?.gte,
      lte: periodBounds?.lte,
      limit: EXISTING_EMISSIONS_PAGE_SIZE,
      offset: 0,
    },
    config: { enabled: isLookupEnabled },
  });

  /**
   * The listing is paginated, so a period holding more emissions than one page
   * means the modal does not know everything that exists. That is not the same
   * as the request failing, and it is not worth retrying: the period has to
   * shrink, so it gets its own reason.
   */
  const isLookupTooLarge =
    !!existingEmissions && existingEmissions.total > existingEmissions.data.length;
  const hasLookupAnswered = !!existingEmissions;

  /** Slots the user has to complete before they can describe an emission. */
  const slotsMissingHours = useMemo(() => findSlotsMissingHours(slots), [slots]);

  /**
   * The emissions that already exist, by the minute their start instant falls
   * in. Candidates are built on Madrid days and hours (RCOMM-6264), so they and
   * the listing share one instant space whatever offset either is written in,
   * and whatever the viewer's timezone.
   *
   * To the minute rather than the millisecond: an emission created by hand at
   * `10:00:37` is the `10:00` one, and generating it again would leave a
   * duplicate only removable one by one.
   */
  const existingStarts = useMemo(() => {
    const starts = new Set<number>();

    (existingEmissions?.data ?? []).forEach(emission => {
      const start = floorToMinute(emission.start_datetime).getTime();

      if (!Number.isNaN(start)) starts.add(start);
    });

    return starts;
  }, [existingEmissions]);

  const candidatesToCreate = useMemo(
    () =>
      candidates.filter(
        candidate => !existingStarts.has(floorToMinute(candidate.startDatetime).getTime())
      ),
    [candidates, existingStarts]
  );

  const skippedCount = candidates.length - candidatesToCreate.length;

  const blockReason = useMemo((): EGenerationBlockReason | null => {
    if (!slots.length) return EGenerationBlockReason.NoSlots;
    if (!periodBounds) return EGenerationBlockReason.NoPeriod;
    if (!candidates.length) {
      // A period that produces nothing because every slot is incomplete is a
      // different problem from one no slot airs in, and it names its own fix.
      return slotsMissingHours.length === slots.length
        ? EGenerationBlockReason.SlotsMissingHours
        : EGenerationBlockReason.NoCandidates;
    }
    // Before the lookup: nothing was asked for a period this size.
    if (isTruncated) return EGenerationBlockReason.TooManyCandidates;
    if (isLookupFetching) return EGenerationBlockReason.LookupPending;
    if (isLookupError) return EGenerationBlockReason.LookupFailed;
    if (!hasLookupAnswered) return EGenerationBlockReason.LookupPending;
    if (isLookupTooLarge) return EGenerationBlockReason.LookupTooLarge;
    if (!candidatesToCreate.length) return EGenerationBlockReason.AllAlreadyExist;
    if (candidatesToCreate.length > PROGRAM_BROADCAST_BATCH_CAP) {
      return EGenerationBlockReason.OverCap;
    }

    return null;
  }, [
    slots.length,
    periodBounds,
    candidates.length,
    isTruncated,
    isLookupFetching,
    isLookupError,
    hasLookupAnswered,
    isLookupTooLarge,
    slotsMissingHours.length,
    candidatesToCreate.length,
  ]);

  const handlePeriodChange = useCallback((nextPeriod: TGenerationPeriod) => {
    setPeriod(nextPeriod);
  }, []);

  return {
    form,
    period,
    onPeriodChange: handlePeriodChange,
    slots,
    candidates,
    candidatesToCreate,
    skippedCount,
    duplicatesDropped,
    slotsMissingHours,
    blockReason,
    canGenerate: blockReason === null,
    isLookupFetching,
    retryLookup: refetchExisting,
  };
}

export type TProgramBroadcastGeneration = ReturnType<typeof useProgramBroadcastGeneration>;
export type { TProgramBroadcastCandidate };
